import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTags, faPlus, faEdit, faTrash, faUpload, faDownload, faSearch, faFilter
} from '@fortawesome/free-solid-svg-icons';
import { useToast } from '../components/ui/ToastProvider';
import { getBrands, createBrand, updateBrand, deleteBrand, Brand } from '../services/brandApi';
import BrandImportModal from '../components/brands/BrandImportModal';

const BrandManagement: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Edit state
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [editName, setEditName] = useState('');
  
  // Add state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      setLoading(true);
      const data = await getBrands({ isActive: showInactive ? undefined : true });
      setBrands(data);
    } catch (error) {
      console.error('Error loading brands:', error);
      showError('Fehler beim Laden der Marken');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBrand = async () => {
    if (!newBrandName.trim()) return;

    try {
      await createBrand({ name: newBrandName.trim(), isActive: true });
      showSuccess('Marke erfolgreich erstellt');
      setNewBrandName('');
      setShowAddForm(false);
      loadBrands();
    } catch (error) {
      console.error('Error creating brand:', error);
      showError('Fehler beim Erstellen der Marke');
    }
  };

  const handleUpdateBrand = async () => {
    if (!editingBrand || !editName.trim()) return;

    try {
      await updateBrand(editingBrand._id, { name: editName.trim() });
      showSuccess('Marke erfolgreich aktualisiert');
      setEditingBrand(null);
      setEditName('');
      loadBrands();
    } catch (error) {
      console.error('Error updating brand:', error);
      showError('Fehler beim Aktualisieren der Marke');
    }
  };

  const handleDeleteBrand = async (brand: Brand) => {
    if (!window.confirm(`Sind Sie sicher, dass Sie die Marke "${brand.name}" löschen möchten?`)) {
      return;
    }

    try {
      await deleteBrand(brand._id);
      showSuccess('Marke erfolgreich gelöscht');
      loadBrands();
    } catch (error) {
      console.error('Error deleting brand:', error);
      showError('Fehler beim Löschen der Marke');
    }
  };

  const handleToggleActive = async (brand: Brand) => {
    try {
      await updateBrand(brand._id, { isActive: !brand.isActive });
      showSuccess(`Marke ${brand.isActive ? 'deaktiviert' : 'aktiviert'}`);
      loadBrands();
    } catch (error) {
      console.error('Error toggling brand status:', error);
      showError('Fehler beim Ändern des Marken-Status');
    }
  };

  const startEdit = (brand: Brand) => {
    setEditingBrand(brand);
    setEditName(brand.name);
  };

  const cancelEdit = () => {
    setEditingBrand(null);
    setEditName('');
  };

  const filteredBrands = brands.filter(brand =>
    brand.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <FontAwesomeIcon icon={faTags} className="text-3xl text-blue-600 mr-4" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Marken-Verwaltung</h1>
            <p className="text-gray-600">Verwalten Sie alle verfügbaren Marken</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
          >
            <FontAwesomeIcon icon={faUpload} className="mr-2" />
            Importieren
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Neue Marke
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FontAwesomeIcon icon={faTags} className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Gesamt</dt>
                  <dd className="text-lg font-medium text-gray-900">{brands.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FontAwesomeIcon icon={faTags} className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Aktiv</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {brands.filter(b => b.isActive).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FontAwesomeIcon icon={faTags} className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Inaktiv</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {brands.filter(b => !b.isActive).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Marken suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => {
                  setShowInactive(e.target.checked);
                  loadBrands();
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Inaktive anzeigen</span>
            </label>
          </div>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Neue Marke hinzufügen</h3>
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="Markenname eingeben..."
              value={newBrandName}
              onChange={(e) => setNewBrandName(e.target.value)}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCreateBrand();
                }
              }}
            />
            <button
              onClick={handleCreateBrand}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Erstellen
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewBrandName('');
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Brands Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Erstellt am
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Aktionen
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                  Lädt Marken...
                </td>
              </tr>
            ) : filteredBrands.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                  Keine Marken gefunden.
                </td>
              </tr>
            ) : (
              filteredBrands.map((brand) => (
                <tr key={brand._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingBrand?._id === brand._id ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleUpdateBrand();
                          } else if (e.key === 'Escape') {
                            cancelEdit();
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <div className="text-sm font-medium text-gray-900">{brand.name}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      brand.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {brand.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(brand.createdAt).toLocaleDateString('de-DE')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {editingBrand?._id === brand._id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleUpdateBrand}
                          className="text-green-600 hover:text-green-900"
                          title="Speichern"
                        >
                          Speichern
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-gray-600 hover:text-gray-900"
                          title="Abbrechen"
                        >
                          Abbrechen
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEdit(brand)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Bearbeiten"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(brand)}
                          className={brand.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                          title={brand.isActive ? 'Deaktivieren' : 'Aktivieren'}
                        >
                          {brand.isActive ? 'Deaktivieren' : 'Aktivieren'}
                        </button>
                        <button
                          onClick={() => handleDeleteBrand(brand)}
                          className="text-red-600 hover:text-red-900"
                          title="Löschen"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Import Modal */}
      <BrandImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => {
          loadBrands();
        }}
      />
    </div>
  );
};

export default BrandManagement;
